import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardActionArea,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import api from '../services/api';
import { toDisplayCard } from '../utils/cardDataStructure';
import NfcPrintThemeCanvas from '../components/nfc-print/NfcPrintThemeCanvas';
import { savedDesignMatchesTheme } from '../utils/nfcPrintThemeFromTemplate';
import { galleryFitMaxWidthForTheme } from '../utils/nfcPrintGalleryFit';

const APP_ORIGIN = 'https://app.webcard.digital';

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;

function isLikelyImageFile(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  return IMAGE_EXT_RE.test(file.name || '');
}

function firstImageFileFromFileList(list) {
  if (!list?.length) return null;
  return Array.from(list).find(isLikelyImageFile) || null;
}

function buildHtml2CanvasOptions(el, themeJson) {
  const bg = themeJson?.tokens?.background || '#0A0E1A';
  return {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: bg,
    width: el.scrollWidth,
    height: el.scrollHeight,
  };
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Raster from html2canvas (often 2×) → exact layout pixel size, e.g. 2102×1346 PNG. */
function canvasToExactPngBlob(sourceCanvas, targetW, targetH) {
  const out = document.createElement('canvas');
  out.width = targetW;
  out.height = targetH;
  const ctx = out.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Could not create canvas'));
  ctx.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height, 0, 0, targetW, targetH);
  return new Promise((resolve, reject) => {
    out.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('PNG export failed'));
      },
      'image/png',
      1
    );
  });
}

async function uploadBannerImageFile(file) {
  const formData = new FormData();
  formData.append('bannerImage', file);
  const { data: result } = await api.post('/upload/banner-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const fullImageUrl = result?.imageUrl?.startsWith('http')
    ? result.imageUrl
    : result?.imageUrl
      ? `https://api.webcard.digital${result.imageUrl}`
      : '';
  if (!fullImageUrl) throw new Error('Upload failed');
  return fullImageUrl;
}

function parseCardData(raw) {
  if (raw == null) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const o = JSON.parse(raw);
      return typeof o === 'object' && o ? o : {};
    } catch {
      return {};
    }
  }
  return {};
}

const DEMO_CARD = {
  name: 'Ritesh Mishra',
  title: 'Project Manager',
  company: 'Logoipsum',
  profileImageUrl: '',
};

/** Gallery preview region height (px); fit width is derived so artboard fits here. */
const GALLERY_PREVIEW_H = 226;

/** Fallback when API is unavailable: single layouts file under public/print-themes. */
async function loadPrintThemesFromIndex() {
  const res = await fetch(`${process.env.PUBLIC_URL || ''}/print-themes/layouts.json`);
  if (!res.ok) throw new Error('Failed to load print layouts');
  const bundle = await res.json();
  const layouts = bundle.layouts || {};
  const defaultId = bundle.defaultLayoutId || 'circuit-tech-nfc';
  const entries = Object.keys(layouts).length
    ? Object.keys(layouts).map((id) => ({
        id,
        name: layouts[id]?.name || id,
        description: '',
      }))
    : [{ id: defaultId, name: defaultId, description: '' }];
  return entries.map((t) => ({
    meta: t,
    theme: layouts[t.id] || layouts[defaultId] || {},
  }));
}

/** Same flow as TemplateSelection: list metadata, then load each theme detail by id. */
async function loadNfcPrintThemeBundles() {
  try {
    const { data: listData } = await api.get('/nfc-print-themes');
    const catalog = listData?.success && Array.isArray(listData.themes) ? listData.themes : [];
    if (!catalog.length) return [];

    const details = await Promise.all(
      catalog.map(async (row) => {
        const { data: detail } = await api.get(`/nfc-print-themes/${row.id}`);
        const t = detail?.theme;
        const themeJson = t?.themeJson && typeof t.themeJson === 'object' ? t.themeJson : {};
        return {
          meta: {
            id: row.id,
            name: row.name,
            description: row.description || '',
            category: row.category || '',
            templateId: row.cardTemplateId ?? null,
            slug: row.slug,
            preview: row.preview ?? null,
          },
          theme: themeJson,
        };
      })
    );
    return details;
  } catch {
    /* fall through */
  }
  return loadPrintThemesFromIndex();
}

function mergePreview(cardData, overrides) {
  const out = { ...cardData };
  if (!overrides || typeof overrides !== 'object') return out;
  for (const k of ['name', 'title', 'company', 'bannerImageUrl']) {
    if (!Object.prototype.hasOwnProperty.call(overrides, k)) continue;
    const raw = overrides[k];
    if (raw == null || String(raw).trim() === '') {
      delete out[k];
    } else {
      out[k] = String(raw).trim();
    }
  }
  return out;
}

function buildDraftFromSaved(cardData, savedOverrides) {
  const o = savedOverrides && typeof savedOverrides === 'object' ? savedOverrides : {};
  const fromSavedOrCard = (key, cardVal) => {
    if (Object.prototype.hasOwnProperty.call(o, key)) {
      return o[key] == null ? '' : String(o[key]);
    }
    return cardVal != null && cardVal !== undefined ? String(cardVal) : '';
  };
  return {
    name: fromSavedOrCard('name', cardData.name),
    title: fromSavedOrCard('title', cardData.title),
    company: fromSavedOrCard('company', cardData.company),
    bannerImageUrl: fromSavedOrCard('bannerImageUrl', cardData.bannerImageUrl || ''),
  };
}

const NfcPrintDesigns = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [themeBundles, setThemeBundles] = useState([]);
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [savedDesign, setSavedDesign] = useState(null);
  const [designLoading, setDesignLoading] = useState(false);

  const [view, setView] = useState('gallery');
  const [previewBundle, setPreviewBundle] = useState(null);
  const [editingTheme, setEditingTheme] = useState(null);
  const [pickingNewTheme, setPickingNewTheme] = useState(false);
  const [themeChangeReturn, setThemeChangeReturn] = useState(null);
  const [draft, setDraft] = useState({ name: '', title: '', company: '', bannerImageUrl: '' });
  const [saving, setSaving] = useState(false);
  const [bannerDropActive, setBannerDropActive] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerFileInputRef = useRef(null);
  const pdfCaptureFrontRef = useRef(null);
  const pdfCaptureBackRef = useRef(null);
  const [imageExportBusy, setImageExportBusy] = useState(false);
  const theme = useTheme();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [subData, setSubData] = useState(null);

  const [qrDataUrl, setQrDataUrl] = useState('');
  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const [bundles, { data }, { data: sub }] = await Promise.all([
          loadNfcPrintThemeBundles(),
          api.get('/cards/my-cards', { params: { limit: 100 } }),
          api.get('/payments/subscription'),
        ]);
        if (cancelled) return;
        setSubData(sub || null);
        setThemeBundles(bundles);
        const rows = data?.cards || [];
        setCards(rows);
        const paramCardId = Number(searchParams.get('cardId'));
        if (Number.isFinite(paramCardId) && rows.some((r) => r.id === paramCardId)) {
          setSelectedCardId(paramCardId);
        } else {
          const first = rows.find((r) => r.cardUrl || r.card_url);
          if (first?.id && !selectedCardId) setSelectedCardId(first.id);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Something went wrong');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedRow = useMemo(
    () => cards.find((c) => c.id === selectedCardId) || null,
    [cards, selectedCardId]
  );

  const cardData = useMemo(() => {
    if (!selectedRow) return DEMO_CARD;
    const parsed = parseCardData(selectedRow.cardData ?? selectedRow.card_data);
    const flat = toDisplayCard(parsed);
    return { ...DEMO_CARD, ...flat };
  }, [selectedRow]);

  const primarySlug = selectedRow ? (selectedRow.cardUrl ?? selectedRow.card_url) : '';
  const publicCardUrl = primarySlug ? `${APP_ORIGIN}/${primarySlug}` : '';

  const nfcLimit = subData?.limits?.nfc ?? 0;
  const nfcUsed = subData?.usage?.nfcDesigns ?? 0;
  const hasSavedForSelected = Boolean(savedDesign?.printThemeId);
  const canSaveDesign = nfcLimit > 0 && (hasSavedForSelected || nfcUsed < nfcLimit);
  const nfcPlanBlocked = nfcLimit === 0;
  const nfcAtLimit = nfcLimit > 0 && !hasSavedForSelected && nfcUsed >= nfcLimit;

  useEffect(() => {
    if (!publicCardUrl) {
      setQrDataUrl('');
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(publicCardUrl, {
      width: 320,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(''); });
    return () => { cancelled = true; };
  }, [publicCardUrl]);

  const fetchSavedDesign = useCallback(async (cardId) => {
    if (!cardId) {
      setSavedDesign(null);
      return;
    }
    setDesignLoading(true);
    try {
      const { data } = await api.get(`/nfc/print-designs/card/${cardId}`);
      setSavedDesign(data?.design || null);
    } catch {
      setSavedDesign(null);
    } finally {
      setDesignLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCardId) fetchSavedDesign(selectedCardId);
  }, [selectedCardId, fetchSavedDesign]);

  const previewForTheme = (meta) => {
    const base = { ...cardData };
    if (savedDesign?.printThemeId && savedDesignMatchesTheme(savedDesign.printThemeId, meta) && savedDesign.layoutOverrides) {
      return mergePreview(base, savedDesign.layoutOverrides);
    }
    return base;
  };

  const openEditor = useCallback((bundle) => {
    if (!pickingNewTheme) {
      const matchesSaved = savedDesign?.printThemeId && savedDesignMatchesTheme(savedDesign.printThemeId, bundle.meta);
      const ov = matchesSaved && savedDesign.layoutOverrides ? savedDesign.layoutOverrides : {};
      setDraft(buildDraftFromSaved(cardData, ov));
    }
    setPickingNewTheme(false);
    setThemeChangeReturn(null);
    setEditingTheme(bundle);
    setView('editor');
  }, [savedDesign, cardData, pickingNewTheme]);

  const handleChangeTemplate = () => {
    if (!editingTheme) return;
    setThemeChangeReturn(editingTheme);
    setPickingNewTheme(true);
    setEditingTheme(null);
    setView('gallery');
  };

  const cancelThemeChange = () => {
    if (themeChangeReturn) {
      setEditingTheme(themeChangeReturn);
      setView('editor');
    }
    setPickingNewTheme(false);
    setThemeChangeReturn(null);
  };

  useEffect(() => {
    if (loading || deepLinkHandledRef.current) return;
    const paramCardId = Number(searchParams.get('cardId'));
    const paramThemeId = Number(searchParams.get('themeId'));
    if (!Number.isFinite(paramCardId) || !Number.isFinite(paramThemeId) || !themeBundles.length) return;
    const bundle = themeBundles.find((b) => b.meta?.id === paramThemeId);
    if (!bundle) return;
    deepLinkHandledRef.current = true;
    setSearchParams({}, { replace: true });
    (async () => {
      setSelectedCardId(paramCardId);
      let design = null;
      try {
        const { data } = await api.get(`/nfc/print-designs/card/${paramCardId}`);
        design = data?.design || null;
        setSavedDesign(design);
      } catch {
        setSavedDesign(null);
      }
      const row = cards.find((c) => c.id === paramCardId);
      const flat = row
        ? { ...DEMO_CARD, ...toDisplayCard(parseCardData(row.cardData ?? row.card_data)) }
        : DEMO_CARD;
      const matchesSaved =
        design?.printThemeId && savedDesignMatchesTheme(design.printThemeId, bundle.meta);
      const ov = matchesSaved && design?.layoutOverrides ? design.layoutOverrides : {};
      setDraft(buildDraftFromSaved(flat, ov));
      setEditingTheme(bundle);
      setView('editor');
    })();
  }, [loading, themeBundles, searchParams, cards, setSearchParams]);

  const closeEditor = () => {
    setEditingTheme(null);
    setView('gallery');
  };

  const mergedEditorPreview = useMemo(
    () => mergePreview(cardData, draft),
    [cardData, draft]
  );

  const handleBannerFile = useCallback(async (file) => {
    if (!file || !isLikelyImageFile(file)) {
      setSnackbar({ open: true, message: 'Please choose an image file (JPG, PNG, etc.).', severity: 'error' });
      return;
    }
    setBannerUploading(true);
    try {
      const url = await uploadBannerImageFile(file);
      setDraft((d) => ({ ...d, bannerImageUrl: url }));
      setSnackbar({ open: true, message: 'Banner image uploaded', severity: 'success' });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || e.message || 'Upload failed',
        severity: 'error',
      });
    } finally {
      setBannerUploading(false);
    }
  }, []);

  const handleBannerInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (e.target) e.target.value = '';
      if (file) handleBannerFile(file);
    },
    [handleBannerFile]
  );

  const handleBannerDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleBannerDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setBannerDropActive(true);
  }, []);

  const handleBannerDragLeave = useCallback((e) => {
    e.preventDefault();
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    setBannerDropActive(false);
  }, []);

  const handleBannerDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setBannerDropActive(false);
      const file = firstImageFileFromFileList(e.dataTransfer?.files);
      if (!file) {
        setSnackbar({ open: true, message: 'Drop an image file here.', severity: 'error' });
        return;
      }
      handleBannerFile(file);
    },
    [handleBannerFile]
  );

  const handleSave = async () => {
    if (!selectedCardId || !editingTheme) return;
    const tid = String(editingTheme.meta?.id ?? '');
    const layoutOverrides = {
      name: draft.name.trim(),
      title: draft.title.trim(),
      company: draft.company.trim(),
      bannerImageUrl: draft.bannerImageUrl.trim(),
    };
    setSaving(true);
    try {
      await api.put(`/nfc/print-designs/card/${selectedCardId}`, {
        printThemeId: tid,
        layoutOverrides,
      });
      setSavedDesign({ printThemeId: tid, layoutOverrides });
      setSubData((prev) => {
        if (!prev) return prev;
        const wasNew = !savedDesign?.printThemeId;
        return wasNew
          ? { ...prev, usage: { ...prev.usage, nfcDesigns: (prev.usage?.nfcDesigns ?? 0) + 1 } }
          : prev;
      });
      setSnackbar({ open: true, message: 'NFC print layout saved', severity: 'success' });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || e.message || 'Save failed',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadNfcImages = useCallback(async () => {
    if (!editingTheme || !selectedCardId || !pdfCaptureFrontRef.current || !pdfCaptureBackRef.current) return;
    setImageExportBusy(true);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const themeJson = editingTheme.theme;
      const pxW = Number(themeJson?.canvas?.width) || 2102;
      const pxH = Number(themeJson?.canvas?.height) || 1346;
      const [cFront, cBack] = await Promise.all([
        html2canvas(pdfCaptureFrontRef.current, buildHtml2CanvasOptions(pdfCaptureFrontRef.current, themeJson)),
        html2canvas(pdfCaptureBackRef.current, buildHtml2CanvasOptions(pdfCaptureBackRef.current, themeJson)),
      ]);
      const [blobFront, blobBack] = await Promise.all([
        canvasToExactPngBlob(cFront, pxW, pxH),
        canvasToExactPngBlob(cBack, pxW, pxH),
      ]);
      const slug = (selectedRow?.cardUrl ?? selectedRow?.card_url ?? `card-${selectedCardId}`).toString();
      const safe = slug.replace(/[^a-z0-9-_]/gi, '-') || 'card';
      triggerBlobDownload(blobFront, `nfc-print-${safe}-front-${pxW}x${pxH}.png`);
      await new Promise((r) => setTimeout(r, 200));
      triggerBlobDownload(blobBack, `nfc-print-${safe}-back-${pxW}x${pxH}.png`);
      setSnackbar({
        open: true,
        message: `Downloaded front and back PNGs (${pxW}×${pxH}px each).`,
        severity: 'success',
      });
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: e?.message || 'Could not export images. Wait for assets to load, then try again.',
        severity: 'error',
      });
    } finally {
      setImageExportBusy(false);
    }
  }, [editingTheme, selectedCardId, selectedRow]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (view === 'editor' && editingTheme) {
    const printPxW = Number(editingTheme.theme?.canvas?.width) || 2102;
    const printPxH = Number(editingTheme.theme?.canvas?.height) || 1346;
    return (
      <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default', py: 4, px: { xs: 2, md: 3 } }}>
        <Box sx={{ maxWidth: { xs: 1100, md: 1480 }, mx: 'auto' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={closeEditor}
            sx={{ mb: 2, textTransform: 'none' }}
          >
            All print templates
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Edit NFC print — {editingTheme.meta?.name || editingTheme.theme?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Changes here are saved for this digital card only and do not change your live card page until you edit the card in My Cards.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {nfcPlanBlocked && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => navigate('/subscription')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  View plans
                </Button>
              }
            >
              NFC print designs require Pro or Business. Upgrade to save layouts for your cards.
            </Alert>
          )}

          {!nfcPlanBlocked && nfcAtLimit && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                nfcLimit < 5 ? (
                  <Button color="inherit" size="small" onClick={() => navigate('/subscription')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Upgrade
                  </Button>
                ) : null
              }
            >
              You have used all {nfcLimit} NFC print design{nfcLimit > 1 ? 's' : ''} on your plan. Pick a card that already has a saved layout, or upgrade for more.
            </Alert>
          )}

          {!nfcPlanBlocked && !nfcAtLimit && nfcLimit > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {nfcUsed} of {nfcLimit} NFC print design{nfcLimit > 1 ? 's' : ''} used on your account (one layout per digital card).
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 3, md: 0 },
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            {/* Print fields — left on md+ */}
            <Box
              sx={{
                width: { xs: '100%', md: 400 },
                maxWidth: { md: 400 },
                flexShrink: 0,
                pr: { md: 3 },
              }}
            >
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Print template
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleChangeTemplate}
                    sx={{ textTransform: 'none', borderRadius: 1 }}
                  >
                    Change
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={editingTheme.meta?.name || editingTheme.theme?.name || 'Print template'}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  {editingTheme.meta?.description ? (
                    <Typography variant="caption" color="text.secondary">
                      {editingTheme.meta.description}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Print fields
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Full name"
                  fullWidth
                  size="small"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  helperText="Shown on the back of the card."
                />
                <TextField
                  label="Company name"
                  fullWidth
                  size="small"
                  value={draft.company}
                  onChange={(e) => setDraft((d) => ({ ...d, company: e.target.value }))}
                  helperText="Shown on the front next to your logo image."
                />
                <TextField
                  label="Position"
                  fullWidth
                  size="small"
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  helperText="Shown on the back under your name."
                />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Front brand image (logo / banner)
                  </Typography>
                  <input
                    ref={bannerFileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleBannerInputChange}
                  />
                  <Box
                    onDragOver={handleBannerDragOver}
                    onDragEnter={handleBannerDragEnter}
                    onDragLeave={handleBannerDragLeave}
                    onDrop={handleBannerDrop}
                    role="region"
                    aria-label="Banner image drop zone"
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      minHeight: 140,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      textAlign: 'center',
                      bgcolor: bannerDropActive
                        ? 'action.hover'
                        : theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.06)
                          : theme.palette.grey[50],
                      border: bannerDropActive ? '2px dashed' : '1px solid',
                      borderColor: bannerDropActive ? 'primary.main' : 'divider',
                      transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    }}
                  >
                    {draft.bannerImageUrl ? (
                      <Box
                        component="img"
                        src={draft.bannerImageUrl}
                        alt=""
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 100,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    ) : (
                      <CloudUploadOutlined sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.85 }} />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {draft.bannerImageUrl
                        ? 'Replace by dropping a new image or choose a file.'
                        : 'Drag and drop an image here, or choose a file.'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={bannerUploading || !selectedCardId}
                        onClick={() => bannerFileInputRef.current?.click()}
                        sx={{ textTransform: 'none' }}
                      >
                        {bannerUploading ? 'Uploading…' : 'Choose image'}
                      </Button>
                      {draft.bannerImageUrl ? (
                        <Tooltip title="Remove banner from this print layout">
                          <IconButton
                            size="small"
                            aria-label="Remove banner image"
                            onClick={() => setDraft((d) => ({ ...d, bannerImageUrl: '' }))}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}>
                    JPG or PNG. Without an image, the front uses your company initial. Upload stores the file on our servers for this print layout.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}
                >
                  <Button
                    variant="contained"
                    disabled={saving || !selectedCardId || !canSaveDesign}
                    onClick={handleSave}
                    sx={{ textTransform: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {saving ? 'Saving…' : 'Save layout'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ImageOutlined />}
                    disabled={!selectedCardId || imageExportBusy}
                    onClick={handleDownloadNfcImages}
                    aria-label="Download NFC print front and back as PNG images"
                    sx={{ textTransform: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {imageExportBusy ? 'Preparing…' : 'Download images'}
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Live preview — right on md+ (matches My Cards: form left, preview right) */}
            <Box
              sx={{
                flex: { md: '1 1 0%' },
                minWidth: 0,
                width: '100%',
                pl: { md: 3 },
                borderLeft: { md: '1px solid' },
                borderColor: { md: 'divider' },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                Live preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Front and back update as you edit.
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  p: 1.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  position: { md: 'sticky' },
                  top: { md: 88 },
                  maxHeight: { md: 'calc(100vh - 110px)' },
                  overflow: { md: 'auto' },
                }}
              >
                <NfcPrintThemeCanvas
                  theme={editingTheme.theme}
                  cardData={mergedEditorPreview}
                  qrDataUrl={selectedRow ? qrDataUrl : ''}
                  qrValue={selectedRow ? publicCardUrl : ''}
                  showFaceLabels
                  cardPixelSize={{
                    width: Number(editingTheme.theme?.canvas?.width) || 2102,
                    height: Number(editingTheme.theme?.canvas?.height) || 1346,
                  }}
                  previewStackVertical
                  maxWidth="none"
                  fitMaxWidth={420}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Full-resolution render targets for PNG export (off-screen) */}
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            left: -20000,
            top: 0,
            zIndex: -20,
            pointerEvents: 'none',
            width: printPxW,
          }}
        >
          <Box ref={pdfCaptureFrontRef} sx={{ width: printPxW }}>
            <NfcPrintThemeCanvas
              theme={editingTheme.theme}
              cardData={mergedEditorPreview}
              qrDataUrl={selectedRow ? qrDataUrl : ''}
              qrValue={selectedRow ? publicCardUrl : ''}
              faceIds={['front']}
              showFaceLabels={false}
              cardPixelSize={{ width: printPxW, height: printPxH }}
              previewStackVertical
              maxWidth="none"
              fitMaxWidth={null}
            />
          </Box>
          <Box ref={pdfCaptureBackRef} sx={{ width: printPxW, mt: 0 }}>
            <NfcPrintThemeCanvas
              theme={editingTheme.theme}
              cardData={mergedEditorPreview}
              qrDataUrl={selectedRow ? qrDataUrl : ''}
              qrValue={selectedRow ? publicCardUrl : ''}
              faceIds={['back']}
              showFaceLabels={false}
              cardPixelSize={{ width: printPxW, height: printPxH }}
              previewStackVertical
              maxWidth="none"
              fitMaxWidth={null}
            />
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          message={snackbar.message}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default', py: 4, px: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: { xs: '100%', lg: 1320 }, mx: 'auto' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          NFC card print designs
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Each theme has a unique NFC print layout. Hover on any card to use or preview.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/nfc/my-print-designs')}
            sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
          >
            My designs
          </Button>
        </Box>
        {pickingNewTheme && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cancelThemeChange} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Cancel
              </Button>
            }
          >
            Choose a new print template. Your name, company, position, and banner image will be kept.
          </Alert>
        )}

        <TextField
          select
          label="Digital card"
          size="small"
          value={selectedCardId || ''}
          onChange={(e) => {
            const v = Number(e.target.value);
            setSelectedCardId(Number.isFinite(v) ? v : null);
            setView('gallery');
            setEditingTheme(null);
            setPickingNewTheme(false);
            setThemeChangeReturn(null);
          }}
          sx={{ minWidth: 260, mb: 3 }}
          disabled={!cards.length || pickingNewTheme}
        >
          {cards.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.cardUrl ?? c.card_url ?? `Card #${c.id}`}
            </MenuItem>
          ))}
        </TextField>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}

        {nfcPlanBlocked && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/subscription')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                View plans
              </Button>
            }
          >
            NFC print designs require Pro or Business. Upgrade to create up to 5 layouts (Business) on one account.
          </Alert>
        )}

        {!nfcPlanBlocked && nfcLimit > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {nfcUsed} of {nfcLimit} NFC print design{nfcLimit > 1 ? 's' : ''} used — each saved layout is linked to one digital card on your email account.
          </Alert>
        )}

        {!selectedRow && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Add a card under My Cards to use your data and QR in previews.
          </Alert>
        )}

        {designLoading && (
          <Box sx={{ mb: 2 }}><CircularProgress size={22} /></Box>
        )}

        <Grid container spacing={2.5} alignItems="stretch">
          {themeBundles.map((bundle) => {
            const prev = previewForTheme(bundle.meta);
            const pw = Number(bundle.theme?.canvas?.width) || 2102;
            const ph = Number(bundle.theme?.canvas?.height) || 1346;
            const galleryFitW = galleryFitMaxWidthForTheme(bundle.theme, GALLERY_PREVIEW_H);
            const templateName = bundle.meta?.name || bundle.theme?.name || 'Print template';
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={bundle.meta.id} sx={{ display: 'flex', minWidth: 0, maxWidth: '100%' }}>
                <Card
                  variant="outlined"
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                      transform: 'translateY(-3px)',
                      '& .nfc-template-overlay': { opacity: 1 },
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => openEditor(bundle)}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      minHeight: 0,
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        height: GALLERY_PREVIEW_H,
                        flexShrink: 0,
                        position: 'relative',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        px: 1,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                      }}
                    >
                      <NfcPrintThemeCanvas
                        theme={bundle.theme}
                        cardData={prev}
                        qrDataUrl={selectedRow ? qrDataUrl : ''}
                        qrValue={selectedRow ? publicCardUrl : ''}
                        faceIds={['front']}
                        showFaceLabels={false}
                        maxWidth="none"
                        cardPixelSize={{ width: pw, height: ph }}
                        previewStackVertical
                        fitMaxWidth={galleryFitW}
                      />
                    </Box>
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        bgcolor: 'background.paper',
                        minHeight: 72,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 0.25,
                          flexWrap: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', minWidth: 0 }} noWrap>
                          {templateName}
                        </Typography>
                        {bundle.meta?.category ? (
                          <Chip
                            label={bundle.meta.category}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.6rem', height: 18, color: 'text.secondary', borderColor: 'divider', flexShrink: 0 }}
                          />
                        ) : null}
                      </Box>
                      {bundle.meta?.description ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.35,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            minHeight: '2.7em',
                          }}
                        >
                          {bundle.meta.description}
                        </Typography>
                      ) : (
                        <Box sx={{ minHeight: '2.7em' }} aria-hidden />
                      )}
                    </Box>
                  </CardActionArea>
                  <Box
                    className="nfc-template-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(16, 46, 92, 0.7)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      opacity: 0,
                      transition: 'opacity 0.25s ease',
                      zIndex: 3,
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, px: 2, textAlign: 'center' }}>
                      {templateName}
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditor(bundle);
                      }}
                      sx={{
                        pointerEvents: 'auto',
                        px: 3,
                        py: 1,
                        borderRadius: '8px',
                        border: 'none',
                        bgcolor: '#00A7E3',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: '#0080B8' },
                      }}
                    >
                      Use NFC Card
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewBundle(bundle);
                      }}
                      sx={{
                        pointerEvents: 'auto',
                        px: 2.5,
                        py: 0.75,
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.5)',
                        bgcolor: 'transparent',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      Preview
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Dialog
          open={!!previewBundle}
          onClose={() => setPreviewBundle(null)}
          maxWidth="md"
          fullWidth
          aria-labelledby="nfc-print-preview-title"
        >
          <DialogTitle id="nfc-print-preview-title">
            {previewBundle?.meta?.name || 'NFC print preview'}
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'grey.100' }}>
            {previewBundle ? (
              <NfcPrintThemeCanvas
                theme={previewBundle.theme}
                cardData={previewForTheme(previewBundle.meta)}
                qrDataUrl={selectedRow ? qrDataUrl : ''}
                qrValue={selectedRow ? publicCardUrl : ''}
                showFaceLabels
                cardPixelSize={{
                  width: Number(previewBundle.theme?.canvas?.width) || 2102,
                  height: Number(previewBundle.theme?.canvas?.height) || 1346,
                }}
                previewStackVertical
                fitMaxWidth={720}
              />
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setPreviewBundle(null)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (previewBundle) openEditor(previewBundle);
                setPreviewBundle(null);
              }}
              sx={{ textTransform: 'none' }}
            >
              Use NFC Card
            </Button>
          </DialogActions>
        </Dialog>

        {themeBundles.length === 0 && !error && (
          <Typography color="text.secondary">
            No NFC print themes returned. Ensure the API is running and the database has been synced, or use public/print-themes/layouts.json as a fallback.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default NfcPrintDesigns;
